import { Component, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import {
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormInputComponent,
} from "../../../shared/components";
import { AttributeValuesComponent } from "../../../shared/components/modals/attribute-values/attribute-values";
import { AttributesValueInputComponent } from "../../../shared/components/modals/attributes-value-input/attributes-value-input";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PageMode } from "../../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../../shared/directives";
import {
  AttributeItemType,
  AttributeModelType,
  AttributeTextMapType,
  TableButtonClicked,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  AttributesApiService,
  PropertiesService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-attributes-editor",
  imports: [
    ReactiveFormsModule,
    FormInputComponent,
    TranslatePipe,
    FormDropdownComponent,
    FormDropdownMultiComponent,
    FormCheckboxComponent,
    FormActionButtonsComponent,
    AttributesValueInputComponent,
    DataTableComponent,
    AttributeValuesComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./attributes-editor.html",
  styleUrl: "./attributes-editor.scss",
})
export class AttributesEditorComponent implements OnInit {
  private readonly _propsService = inject(PropertiesService);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _attributesApiService = inject(AttributesApiService);
  private readonly _smartToolTipsService = inject(SmartToolTipsService);
  private readonly _router = inject(Router);
  private readonly _validatorApiService = inject(ValidatorApiService);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  attributesForm: FormGroup = new FormGroup({});
  readonly pageMode = input.required<string>();
  readonly id = input<number | undefined>(undefined);

  submitted = signal<boolean>(false);
  showTable = signal<boolean>(false);
  attribute = signal<AttributeModelType | undefined>(undefined);
  records = signal<number | string>("");
  fieldType = signal<"text" | "dropdown" | "date">("text");
  dropdownData = signal<ValueText[]>([]);
  defaultValue = signal<string | undefined>(undefined);
  values = signal<AttributeTextMapType[]>([]);
  selectedValue = signal<Partial<AttributeTextMapType> | undefined>(undefined);
  selectedItemTypes = signal<AttributeItemType[]>([]);
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  dataTypeOptions = signal<ValueText[]>([]);
  itemTypeOptions = signal<ValueText[]>([]);

  input = viewChild.required<AttributesValueInputComponent>("input");
  attributeValues = viewChild.required<AttributeValuesComponent>("valuesInput");

  public get displayLabel(): string {
    const nameEN = this.attributesForm.get("nameEN")?.value;
    const nameAE = this.attributesForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();

  readonly AdaaHelper = AdaaHelper;
  readonly PageMode = PageMode;
  readonly Constants = Constants;

  public ngOnInit(): void {
    this._prepareForm();

    this._initPage();

    if (this.pageMode() !== PageMode.view)
      this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_ATTRIBUTES);

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._router.navigateByUrl("/console");
    });
  }

  private _initPage() {
    forkJoin({
      attributes: this._propsService.getPropById(Constants.CONSTANT_MEASUREMENT_UNIT_ATTRIBUTES),
      itemTypes: this._propsService.getAllItemTypes(),
    }).subscribe({
      next: (results) => {
        this.dataTypeOptions.set(
          AdaaHelper.setDropdownArray(results.attributes.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );
        this.itemTypeOptions.set(
          AdaaHelper.setDropdownArray(results.itemTypes.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );
      },
      complete: () => {
        this._getAttribute();
      },
    });
  }

  private _getAttribute() {
    const id = this.id();
    if (!id) return;
    this._attributesApiService.getAttribute(+id).subscribe({
      next: (response) => {
        this.attribute.set(response.responseData);
        this.attributesForm.patchValue(response.responseData);
        this.attributesForm
          .get("attrItemTypes")
          ?.patchValue(response.responseData.attrItemTypes?.map((item) => item.itemType));
        if (response.responseData.attrItemTypes) this.selectedItemTypes.set(response.responseData.attrItemTypes);
      },
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.attributesForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },

      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      dataType: { value: null, disabled: isViewMode },
      available: { value: null, disabled: isViewMode },
      mandatory: { value: null, disabled: isViewMode },
      attrItemTypes: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_ATTRIBUTES, this.attributesForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.attributesForm = response.form;
        },
      });

    this.attributesForm
      .get("dataType")
      ?.valueChanges.pipe(this._untilDestroyed())
      .subscribe((value) => {
        switch (value) {
          case Constants.CONSTANT_ATTRIBUTES_DATATYPE_TEXT_MAPPING:
            this.showTable.set(true);
            this.fieldType.set("dropdown");
            break;

          case Constants.CONSTANT_ATTRIBUTES_DATATYPE_FREE_TEXT:
          case Constants.CONSTANT_ATTRIBUTES_DATATYPE_CURRENCY:
          case Constants.CONSTANT_ATTRIBUTES_DATATYPE_PERCENTAGE:
            this.showTable.set(false);
            this.fieldType.set("text");
            break;

          case Constants.CONSTANT_ATTRIBUTES_DATATYPE_DATE:
            this.showTable.set(false);
            this.fieldType.set("date");
            break;

          default:
            break;
        }
      });
  }

  public checkInvalid(control: string) {
    return (
      this.attributesForm.controls[control].errors?.["required"] &&
      (this.attributesForm.get(control)?.dirty || this.submitted())
    );
  }

  public setMandatory() {
    const value = this.attributesForm.get("mandatory")?.value;
    if (value === AdaaBoolean.Y) {
      this.attributesForm.get("available")?.patchValue(AdaaBoolean.Y);
      this.attributesForm.get("available")?.disable();
    }
    if (value === AdaaBoolean.N) {
      this.attributesForm.get("available")?.patchValue(AdaaBoolean.N);
      this.attributesForm.get("available")?.enable();
    }
  }

  public save() {
    this.submitted.set(true);
    if (this.attributesForm.invalid) {
      this._toastrService.error(this._translateService.instant("common.form.label.missing_information"));
      return;
    }

    if (this.attributesForm.get("mandatory")?.value === AdaaBoolean.Y) {
      const typeIds = this.attributesForm.get("attrItemTypes")?.value;
      this._attributesApiService.getCountByType(typeIds).subscribe({
        next: (response) => {
          this.records.set(response.responseData);
          const options = AdaaHelper.setDropdownArray(
            this.values(),
            "lovNumber",
            AdaaHelper.getFieldLanguage("lovText")
          );
          this.dropdownData.set(options);
          this.input().open();
        },
      });
    } else this.submit();
  }

  public submit() {
    const selectedTypeIds: number[] = this.attributesForm.value.attrItemTypes ?? [];
    const attrItemTypes = selectedTypeIds.map((attrItem: number) => {
      const value = this.selectedItemTypes().find((selectedItem) => selectedItem.itemType === attrItem);
      const newItem = {
        itemType: attrItem,
        status: Constants.OBJECT_STATUS.DRAFT,
      };
      return value ?? newItem;
    });
    const result = {
      ...this.attribute(),
      ...this.attributesForm.value,
      attrItemTypes,
      available:
        this.attributesForm.value.mandatory === AdaaBoolean.Y ? AdaaBoolean.Y : this.attributesForm.value.available,
      libExtAttrLovs: this.values(),
      retroactive: AdaaBoolean.Y,
      status: Constants.OBJECT_STATUS.ACTIVE,
      defaultValue: this.defaultValue(),
    };

    this._attributesApiService.saveAttribute(this.pageMode() === PageMode.edit, result).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/attributes");
      },
    });
  }

  public cancel() {
    this._router.navigateByUrl("/console/attributes");
  }

  public addValue(data: AttributeTextMapType) {
    const values: AttributeTextMapType[] = AdaaHelper.clone(this.values());
    if (this.attributeValues().pageMode === PageMode.edit) {
      const index = data.displayOrder - 1;
      values.splice(index, 1, data);
    } else {
      data.displayOrder = this.values().length + 1;
      data.status = Constants.OBJECT_STATUS.DRAFT;
      values.push(data);
    }
    this.values.set(values);
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "edit":
        this.selectedValue.set(value.data);
        this.attributeValues().pageMode = PageMode.edit;
        this.attributeValues().open();
        break;
      case "delete": {
        const values = AdaaHelper.clone(this.values());
        values.splice(value.index, 1);
        this.values.set(values);
        break;
      }

      default:
        break;
    }
  }
}
